import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBrands, useBrandModels, useCreateBrand, useCreateModel } from "./hooks";
import { Input } from "@/shared/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SELECT_NONE,
} from "@/shared/ui/Select";

interface InlineProductAddModalProps {
  open: boolean;
  // Only car / technology have a catalogue — the trigger guarantees one of these two.
  loanType: "car" | "technology";
  // The operator-typed name, used to prefill the "new model" input for convenience.
  initialName?: string;
  onClose: () => void;
  // Receives the canonical "Brand Model"; the caller writes it into the row name (triggers re-validate).
  onPicked: (canonical: string) => void;
}

// Two-step (brand → model) inline catalogue-add dialog. Reuses the ConfirmModal a11y recipe
// (portal-to-body, role="dialog", capture-phase Escape, nested-safe inert focus trap) because it
// opens on top of the import preview, not the drawer — but the recipe is nested-safe either way.
export function InlineProductAddModal({
  open,
  loanType,
  initialName,
  onClose,
  onPicked,
}: InlineProductAddModalProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  // The first focusable field is the brand Select trigger (a <button>, not a native <select>).
  const firstFieldRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [newBrandName, setNewBrandName] = useState("");
  const [newModelName, setNewModelName] = useState("");
  // Track the picked model as {id, canonical} so confirm has the canonical even before the list refetches.
  const [selectedModel, setSelectedModel] = useState<{ id: number; canonical: string } | null>(null);

  const brandsQuery = useBrands(loanType, open);
  const modelsQuery = useBrandModels(loanType, selectedBrandId, open);
  const { mutate: addBrand, isPending: addingBrand } = useCreateBrand();
  const { mutate: addModel, isPending: addingModel } = useCreateModel();

  const brands = brandsQuery.data ?? [];
  const models = modelsQuery.data ?? [];

  // Reset all local state each time the modal opens; prefill the model input with the typed name.
  useEffect(() => {
    if (!open) return;
    setSelectedBrandId(null);
    setNewBrandName("");
    setNewModelName(initialName?.trim() ?? "");
    setSelectedModel(null);
  }, [open, initialName]);

  // Escape (capture phase) — preempt any parent document-level handler so one Escape closes only this.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  // Focus trap (nested-safe) + restore — identical recipe to ConfirmModal.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const wrapper = wrapperRef.current;
    const silenced: HTMLElement[] = [];
    for (const node of Array.from(document.body.children)) {
      if (node === wrapper) continue;
      const el = node as HTMLElement;
      if (el.hasAttribute("inert")) continue;
      el.setAttribute("inert", "");
      el.setAttribute("aria-hidden", "true");
      silenced.push(el);
    }
    firstFieldRef.current?.focus();
    return () => {
      for (const el of silenced) {
        el.removeAttribute("inert");
        el.removeAttribute("aria-hidden");
      }
      restoreRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  // Radix Select hands back a string; SELECT_NONE is the empty/placeholder sentinel (ids are numbers).
  function handleSelectBrand(value: string) {
    const id = value === SELECT_NONE ? null : Number(value);
    setSelectedBrandId(id);
    setSelectedModel(null);
  }

  function handleSelectModel(value: string) {
    const id = value === SELECT_NONE ? null : Number(value);
    const m = models.find((x) => x.id === id);
    setSelectedModel(m ? { id: m.id, canonical: m.canonical } : null);
  }

  function handleAddBrand() {
    const name = newBrandName.trim();
    if (!name || addingBrand) return;
    addBrand(
      { type: loanType, name },
      {
        onSuccess: (res) => {
          // Select the new (or resolved) brand and clear the model step.
          setSelectedBrandId(res.id);
          setSelectedModel(null);
          setNewBrandName("");
        },
      },
    );
  }

  function handleAddModel() {
    const name = newModelName.trim();
    if (!name || selectedBrandId === null || addingModel) return;
    addModel(
      { type: loanType, brandId: selectedBrandId, name },
      {
        onSuccess: (res) => {
          setSelectedModel({ id: res.id, canonical: res.canonical });
          setNewModelName("");
        },
      },
    );
  }

  function handleConfirm() {
    if (!selectedModel) return;
    onPicked(selectedModel.canonical);
    onClose();
  }

  const brandStepDone = selectedBrandId !== null;

  return createPortal(
    <div ref={wrapperRef} className="fixed inset-0 z-[60] grid place-items-center p-4">
      <div className="modal-scrim absolute inset-0 bg-grey-900/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="modal-pop relative w-full max-w-[440px] rounded-card bg-white p-5 shadow-float"
      >
        <h3 id={titleId} className="text-[15px] font-bold text-grey-900">
          {t("import.agreements.catalogue.modalTitle")}
        </h3>

        {/* Step 1 — brand */}
        <div className="mt-4">
          <label className="mb-1.5 block text-[13px] font-semibold text-grey-700">
            {t("import.agreements.catalogue.selectBrand")}
          </label>
          <Select
            value={selectedBrandId === null ? SELECT_NONE : String(selectedBrandId)}
            onValueChange={handleSelectBrand}
            disabled={brandsQuery.isLoading}
          >
            <SelectTrigger ref={firstFieldRef} className="text-[13px]">
              <SelectValue placeholder={t("import.agreements.catalogue.selectBrand")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_NONE}>
                {t("import.agreements.catalogue.selectBrand")}
              </SelectItem>
              {brands.map((b) => (
                <SelectItem key={b.id} value={String(b.id)}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 flex gap-2">
            <Input
              type="text"
              value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              placeholder={t("import.agreements.catalogue.newBrand")}
              className="min-w-0 flex-1 text-[13px]"
            />
            <button
              type="button"
              onClick={handleAddBrand}
              disabled={!newBrandName.trim() || addingBrand}
              className="flex flex-none items-center gap-1 rounded-control border border-grey-300 bg-white px-3 text-[13px] font-semibold text-grey-700 hover:bg-grey-50 disabled:opacity-60"
            >
              {addingBrand ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {t("import.agreements.catalogue.add")}
            </button>
          </div>
        </div>

        {/* Step 2 — model (locked until a brand is chosen) */}
        <div className={`mt-4 ${brandStepDone ? "" : "pointer-events-none opacity-50"}`}>
          <label className="mb-1.5 block text-[13px] font-semibold text-grey-700">
            {t("import.agreements.catalogue.selectModel")}
          </label>
          <Select
            value={selectedModel ? String(selectedModel.id) : SELECT_NONE}
            onValueChange={handleSelectModel}
            disabled={!brandStepDone || modelsQuery.isLoading}
          >
            <SelectTrigger className="text-[13px]">
              <SelectValue placeholder={t("import.agreements.catalogue.selectModel")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_NONE}>
                {t("import.agreements.catalogue.selectModel")}
              </SelectItem>
              {models.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 flex gap-2">
            <Input
              type="text"
              value={newModelName}
              onChange={(e) => setNewModelName(e.target.value)}
              placeholder={t("import.agreements.catalogue.newModel")}
              disabled={!brandStepDone}
              className="min-w-0 flex-1 text-[13px]"
            />
            <button
              type="button"
              onClick={handleAddModel}
              disabled={!newModelName.trim() || !brandStepDone || addingModel}
              className="flex flex-none items-center gap-1 rounded-control border border-grey-300 bg-white px-3 text-[13px] font-semibold text-grey-700 hover:bg-grey-50 disabled:opacity-60"
            >
              {addingModel ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {t("import.agreements.catalogue.add")}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 items-center rounded-control border border-grey-300 bg-white px-3.5 text-[13px] font-semibold text-grey-700 hover:bg-grey-50"
          >
            {t("import.agreements.catalogue.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedModel}
            className="flex h-9 items-center rounded-control bg-green px-3.5 text-[13px] font-semibold text-white hover:bg-green-dark disabled:opacity-60"
          >
            {t("import.agreements.catalogue.confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
